import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";

import { useRootData } from "~/hooks/use-root-data";
import { useAppConfig } from "~/routes/api.app-config";
import type { AppAbility } from "./ability";
import { defineAbilityFor } from "./ability";

import { datadogRum } from "@datadog/browser-rum";

const AbilityContext = createContext<Pick<AppAbility, "can" | "cannot"> | null>(
  null,
);

export const useAbility = () => {
  const context = useContext(AbilityContext);
  if (!context) {
    throw new Error("Missing AbilityContextProvider");
  }

  return context;
};

export const APP_CONFIG_QUERY_KEY = "appConfig";

export const AbilityProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const rootData = useRootData();

  const { data: appConfig } = useAppConfig();

  const ability = defineAbilityFor(
    rootData?.user,
    appConfig,
    rootData?.featureToggleOverride,
  );

  const role =
    (rootData?.user?.rbac.isDeveloper && "developer") ||
    (rootData?.user?.rbac.isInTestGroup && "test-group") ||
    "all";

  return (
    <AbilityContext.Provider
      value={{
        can: (...[action, featureFlagPath]: Parameters<AppAbility["can"]>) => {
          const result = ability.can(action, featureFlagPath);
          datadogRum.addFeatureFlagEvaluation(
            `${action}_${featureFlagPath}`,
            result && role,
          );
          return result;
        },
        cannot: (
          ...[action, featureFlagPath]: Parameters<AppAbility["cannot"]>
        ) => {
          const result = ability.cannot(action, featureFlagPath);
          datadogRum.addFeatureFlagEvaluation(
            `${action}_${featureFlagPath}`,
            !result && role,
          );
          return result;
        },
      }}
    >
      {children}
    </AbilityContext.Provider>
  );
};
